import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import {
  AlertCircle,
  BarChart,
  CalendarIcon,
  ChevronLeft,
  Clock,
  ExternalLink,
  FileText,
  Heart,
  Image,
  Info,
  Link,
  Mail,
  MapPin,
  MessageSquare,
  Pencil,
  Phone,
  Search,
  SendHorizonal,
  User,
  Video,
  XCircle,
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { CommunityEvent } from "./CommunityEvents";

export enum CommunityEventUpdateType {
  EVENT_UPDATE = "EVENT_UPDATE",
  EVENT_PHOTOS = "EVENT_PHOTOS",
  EVENT_BROCHURES = "EVENT_BROCHURES",
  EVENT_LOCATION = "EVENT_LOCATION",
  EVENT_MEET_LINK = "EVENT_MEET_LINK",
  EVENT_HOST_DETAILS = "EVENT_HOST_DETAILS",
  EVENT_COLLATERALS = "EVENT_COLLATERALS",
  THANKYOU = "THANKYOU",
  POST_EVENT_IMPACT = "POST_EVENT_IMPACT",
  VOLUNTEER_SUBMISSION_LINK = "VOLUNTEER_SUBMISSION_LINK",
  EVENT_CANCELLED = "EVENT_CANCELLED",
  EVENT_POSTPONED = "EVENT_POSTPONED",
  OTHERS = "OTHERS",
}

interface Participant {
  id: number;
  name: string;
  email: string;
  phone: string;
  picture_url: string | null;
  status: string;
  remarks: string | null;
  meta?: {
    experience?: string;
    availability?: string;
    registeredAt?: string;
    termsAccepted?: boolean;
    additionalInfo?: string;
    transportation?: string;
    waiverAccepted?: boolean;
    emergencyContact?: {
      name?: string;
      phone?: string;
      relationship?: string;
    };
    dietaryRestrictions?: string;
  };
}

interface ParticipantsResponse {
  data: Participant[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface CommunityEventResponse {
  data: CommunityEvent;
}

interface EventUpdate {
  id: number;
  event_id: number;
  update_type: CommunityEventUpdateType;
  update_content: string;
  attachment_url?: string;
  created_by: string;
  created_at: string;
}

interface EventUpdatesResponse {
  data: EventUpdate[];
  message: string;
}

interface CreateEventUpdateRequest {
  updateType: CommunityEventUpdateType;
  content: string;
  attachment_url?: string;
  sendEmailToParticipants?: boolean;
}

interface CreateEventUpdateResponse {
  data: EventUpdate;
  message: string;
}

function ParticipantsList({ eventId, session, profile }: { eventId: string, session: any, profile: any }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [addParticipantOpen, setAddParticipantOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [newParticipant, setNewParticipant] = useState({
    fullName: "",
    email: "",
    phone: "",
    created_at: new Date().toISOString(),
  });
  const limit = 10;
  
  const { data: participants, isLoading } = useQuery<ParticipantsResponse>({
    queryKey: ["participants", eventId, page],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/campaign/community-events/volunteer/${eventId}?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch participants");
      }
      return response.json();
    },
    enabled: !!eventId && !!session?.access_token,
  });

  const addParticipantMutation = useMutation({
    mutationFn: async (data: { fullName: string; email: string; phone: string; created_at: string }) => {
      const requestBody = {
        eventId: eventId,
        name: data.fullName,
        email: data.email,
        phone: data.phone,
        created_at: data.created_at
      };

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/campaign/community-events/volunteer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add participant");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["participants", eventId] });
      setAddParticipantOpen(false);
      setNewParticipant({
        fullName: "",
        email: "",
        phone: "",
        created_at: new Date().toISOString(),
      });
      toast({
        title: "Success",
        description: "Participant added successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add participant",
        variant: "destructive",
      });
    },
  });

  const handleAddParticipant = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newParticipant.fullName || !newParticipant.email) {
      toast({
        title: "Error",
        description: "Name and email are required",
        variant: "destructive",
      });
      return;
    }
    addParticipantMutation.mutate(newParticipant);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'signed_up':
        return 'bg-green-500/10 text-green-700 border-green-300';
      case 'waitlisted':
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-300';
      case 'cancelled':
        return 'bg-red-500/10 text-red-700 border-red-300';
      default:
        return '';
    }
  };

  const filteredParticipants = useMemo(() => {
    if (!participants?.data || !searchQuery.trim()) {
      return participants?.data || [];
    }
    
    const query = searchQuery.toLowerCase().trim();
    return participants.data.filter(participant => 
      participant.name.toLowerCase().includes(query) ||
      participant.email.toLowerCase().includes(query) ||
      participant.phone.toLowerCase().includes(query) ||
      participant.status.toLowerCase().includes(query) ||
      participant.meta?.availability?.toLowerCase().includes(query) ||
      participant.meta?.transportation?.toLowerCase().includes(query)
    );
  }, [participants?.data, searchQuery]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold">Participants</h3>
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle>Participants</CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setAddParticipantOpen(true)}
            className="bg-white"
          >
            <User className="h-4 w-4 mr-2" />
            Add Participant
          </Button>
        </div>
        <CardDescription>
          Manage and view all participants registered for this event
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              {participants?.pagination.total || 0} total
            </Badge>
          </div>
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search participants..."
              className="w-full bg-white pl-8 pr-4"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <div className="space-y-6 h-[600px] overflow-y-auto pr-4">
          {filteredParticipants.length > 0 ? (
            filteredParticipants.map((participant) => (
              <div key={participant.id} className="border-b pb-4 last:border-0 last:pb-0">
                <div className="flex items-start gap-4">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={participant.picture_url || undefined} />
                    <AvatarFallback>{participant.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium">{participant.name}</h3>
                      <Badge 
                        variant="outline" 
                        className={`capitalize ${getStatusColor(participant.status)}`}
                      >
                        {participant.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        {participant.email}
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        {participant.phone}
                      </div>
                      <div className="text-xs">
                        Registered {(() => {
                          try {
                            return participant.meta?.registeredAt 
                              ? format(new Date(participant.meta.registeredAt), "MMM d, yyyy 'at' h:mm a")
                              : "on unknown date";
                          } catch (e) {
                            return "on unknown date";
                          }
                        })()}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {participant.meta?.availability && (
                          <Badge variant="secondary" className="text-xs">
                            {participant.meta.availability}
                          </Badge>
                        )}
                        {participant.meta?.transportation && (
                          <Badge variant="secondary" className="text-xs">
                            {participant.meta.transportation} transport
                          </Badge>
                        )}
                        {participant.meta?.dietaryRestrictions && (
                          <Badge variant="secondary" className="text-xs">
                            Dietary restrictions
                          </Badge>
                        )}
                      </div>

                      {participant.meta?.emergencyContact && (
                        <div className="mt-2 text-sm">
                          <p className="text-muted-foreground">Emergency Contact:</p>
                          <p>
                            {participant.meta.emergencyContact.name || 'Unknown'} 
                            {participant.meta.emergencyContact.relationship ? `(${participant.meta.emergencyContact.relationship})` : ''} 
                            {participant.meta.emergencyContact.phone ? `- ${participant.meta.emergencyContact.phone}` : ''}
                          </p>
                        </div>
                      )}
                      
                      {participant.meta?.additionalInfo && (
                        <div className="mt-2">
                          <p className="text-sm text-muted-foreground">Additional Information:</p>
                          <p className="text-sm">{participant.meta.additionalInfo}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              {searchQuery.trim() 
                ? "No participants match your search" 
                : "No participants registered yet"}
            </div>
          )}
        </div>

        {participants?.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {page} of {participants.pagination.totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === participants.pagination.totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </CardContent>

      {/* Add Participant Dialog */}
      <Dialog open={addParticipantOpen} onOpenChange={setAddParticipantOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Participant</DialogTitle>
            <DialogDescription>
              Add a new participant to this event
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddParticipant}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="fullName">Full Name*</Label>
                <Input
                  id="fullName"
                  value={newParticipant.fullName}
                  onChange={(e) => setNewParticipant({...newParticipant, fullName: e.target.value})}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">Email*</Label>
                <Input
                  id="email"
                  type="email"
                  value={newParticipant.email}
                  onChange={(e) => setNewParticipant({...newParticipant, email: e.target.value})}
                  placeholder="john@example.com"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={newParticipant.phone}
                  onChange={(e) => setNewParticipant({...newParticipant, phone: e.target.value})}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddParticipantOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addParticipantMutation.isPending}>
                {addParticipantMutation.isPending ? "Adding..." : "Add Participant"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function EventUpdates({ eventId, session, profile }: { eventId: string, session: any, profile: any }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateType, setUpdateType] = useState<CommunityEventUpdateType | "">("");
  const [updateContent, setUpdateContent] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [sendEmailToParticipants, setSendEmailToParticipants] = useState(false);
  const [isSubmittingUpdate, setIsSubmittingUpdate] = useState(false);

  // Fetch event updates
  const { data: eventUpdates, isLoading: isUpdatesLoading } = useQuery<EventUpdatesResponse>({
    queryKey: ["event-updates", eventId],
    queryFn: async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_CC_API_URL}/v1/campaign/${eventId}/updates`,
          {
            headers: {
              Authorization: `Bearer ${session?.access_token}`,
            },
          }
        );
        
        if (!response.ok) {
          return { data: [], message: "Failed to fetch updates" }; 
        }
        
        return response.json();
      } catch (error) {
        console.error("Failed to fetch updates:", error);
        return { data: [], message: "Failed to fetch updates" };
      }
    },
    enabled: !!eventId && !!session?.access_token,
  });

  // Mutation to create a new event update
  const createEventUpdate = useMutation({
    mutationFn: async (updateData: CreateEventUpdateRequest) => {
      setIsSubmittingUpdate(true);
      
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/campaign/${eventId}/updates`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify(updateData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create event update');
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Event update sent successfully",
      });
      setIsSubmittingUpdate(false);
      setShowUpdateForm(false);
      resetUpdateForm();
      queryClient.invalidateQueries({ queryKey: ["event-updates", eventId] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send event update",
        variant: "destructive",
      });
      setIsSubmittingUpdate(false);
    },
  });
  
  // Reset update form fields
  const resetUpdateForm = () => {
    setUpdateType("");
    setUpdateContent("");
    setAttachmentUrl("");
    setSendEmailToParticipants(false);
  };
  
  // Submit update form
  const handleSubmitUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!updateType || !updateContent) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }
    
    createEventUpdate.mutate({
      updateType: updateType as CommunityEventUpdateType,
      content: updateContent,
      attachment_url: attachmentUrl || undefined,
      sendEmailToParticipants,
    });
  };

  // Helper function to get icon for update type
  const getUpdateTypeIcon = (type: string) => {
    switch (type) {
      case CommunityEventUpdateType.EVENT_PHOTOS:
        return <Image className="h-5 w-5" />;
      case CommunityEventUpdateType.EVENT_BROCHURES:
        return <FileText className="h-5 w-5" />;
      case CommunityEventUpdateType.EVENT_LOCATION:
        return <MapPin className="h-5 w-5" />;
      case CommunityEventUpdateType.EVENT_MEET_LINK:
        return <Video className="h-5 w-5" />;
      case CommunityEventUpdateType.EVENT_HOST_DETAILS:
        return <User className="h-5 w-5" />;
      case CommunityEventUpdateType.EVENT_UPDATE:
        return <Info className="h-5 w-5" />;
      case CommunityEventUpdateType.EVENT_COLLATERALS:
        return <FileText className="h-5 w-5" />;
      case CommunityEventUpdateType.THANKYOU:
        return <Heart className="h-5 w-5" />;
      case CommunityEventUpdateType.POST_EVENT_IMPACT:
        return <BarChart className="h-5 w-5" />;
      case CommunityEventUpdateType.VOLUNTEER_SUBMISSION_LINK:
        return <ExternalLink className="h-5 w-5" />;
      case CommunityEventUpdateType.EVENT_CANCELLED:
        return <XCircle className="h-5 w-5" />;
      case CommunityEventUpdateType.EVENT_POSTPONED:
        return <AlertCircle className="h-5 w-5" />;
      default:
        return <MessageSquare className="h-5 w-5" />;
    }
  };
  
  // Helper function to get human-readable update type
  const getUpdateTypeLabel = (type: string) => {
    switch (type) {
      case CommunityEventUpdateType.EVENT_PHOTOS:
        return "Event Photos";
      case CommunityEventUpdateType.EVENT_BROCHURES:
        return "Event Brochures";
      case CommunityEventUpdateType.EVENT_LOCATION:
        return "Location Change";
      case CommunityEventUpdateType.EVENT_MEET_LINK:
        return "Meeting Link";
      case CommunityEventUpdateType.EVENT_HOST_DETAILS:
        return "Host Information";
      case CommunityEventUpdateType.EVENT_UPDATE:
        return "General Update";
      case CommunityEventUpdateType.EVENT_COLLATERALS:
        return "Event Collaterals";
      case CommunityEventUpdateType.THANKYOU:
        return "Thank You Message";
      case CommunityEventUpdateType.POST_EVENT_IMPACT:
        return "Impact Report";
      case CommunityEventUpdateType.VOLUNTEER_SUBMISSION_LINK:
        return "Volunteer Submission";
      case CommunityEventUpdateType.EVENT_POSTPONED:
        return "Event Postponed";
      case CommunityEventUpdateType.OTHERS:
        return "Other Update";
      default:
        return type.replace(/_/g, ' ').toLowerCase();
    }
  };

  // Determine if the update type should show an upload button
  const shouldShowUploadButton = () => {
    return [
      CommunityEventUpdateType.EVENT_PHOTOS,
      CommunityEventUpdateType.EVENT_BROCHURES,
      CommunityEventUpdateType.EVENT_COLLATERALS,
      CommunityEventUpdateType.POST_EVENT_IMPACT,
    ].includes(updateType as CommunityEventUpdateType);
  };

  // Get attachment field label based on update type
  const getAttachmentFieldLabel = () => {
    switch (updateType) {
      case CommunityEventUpdateType.EVENT_PHOTOS:
        return "Photo Link";
      case CommunityEventUpdateType.EVENT_BROCHURES:
        return "Brochure PDF Link";
      case CommunityEventUpdateType.EVENT_LOCATION:
        return "Map Link";
      case CommunityEventUpdateType.EVENT_MEET_LINK:
        return "Meeting Link";
      case CommunityEventUpdateType.EVENT_COLLATERALS:
        return "Collateral Files Link";
      case CommunityEventUpdateType.VOLUNTEER_SUBMISSION_LINK:
        return "Submission Form Link";
      default:
        return "Attachment URL (optional)";
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-xl">Updates & Communications</CardTitle>
            <CardDescription>Send and view updates for this event</CardDescription>
          </div>
          {!showUpdateForm && (
            <Button 
              onClick={() => setShowUpdateForm(true)}
              className="bg-primary hover:bg-primary/90"
            >
              <SendHorizonal className="mr-2 h-4 w-4" />
              New Update
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {showUpdateForm ? (
          <div className="mb-8 border rounded-lg p-4 bg-slate-50">
            <h3 className="text-lg font-medium mb-4">Create New Update</h3>
            <form onSubmit={handleSubmitUpdate}>
              <div className="grid gap-4">
                <div className="grid grid-cols-1 gap-2">
                  <Label htmlFor="updateType">Update Type*</Label>
                  <Select 
                    value={updateType} 
                    onValueChange={(value) => setUpdateType(value as CommunityEventUpdateType)}
                  >
                    <SelectTrigger id="updateType">
                      <SelectValue placeholder="Select update type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CommunityEventUpdateType.EVENT_UPDATE}>General Update</SelectItem>
                      <SelectItem value={CommunityEventUpdateType.EVENT_PHOTOS}>Event Photos</SelectItem>
                      <SelectItem value={CommunityEventUpdateType.EVENT_LOCATION}>Location Change</SelectItem>
                      <SelectItem value={CommunityEventUpdateType.EVENT_MEET_LINK}>Meeting Link</SelectItem>
                      <SelectItem value={CommunityEventUpdateType.EVENT_HOST_DETAILS}>Host Information</SelectItem>
                      <SelectItem value={CommunityEventUpdateType.EVENT_BROCHURES}>Event Brochures</SelectItem>
                      <SelectItem value={CommunityEventUpdateType.EVENT_COLLATERALS}>Event Collaterals</SelectItem>
                      <SelectItem value={CommunityEventUpdateType.THANKYOU}>Thank You Message</SelectItem>
                      <SelectItem value={CommunityEventUpdateType.POST_EVENT_IMPACT}>Impact Report</SelectItem>
                      <SelectItem value={CommunityEventUpdateType.VOLUNTEER_SUBMISSION_LINK}>Volunteer Submission</SelectItem>
                      <SelectItem value={CommunityEventUpdateType.EVENT_POSTPONED}>Event Postponed</SelectItem>
                      <SelectItem value={CommunityEventUpdateType.OTHERS}>Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-1 gap-2">
                  <Label htmlFor="updateContent">Content*</Label>
                  <Textarea 
                    id="updateContent"
                    value={updateContent}
                    onChange={(e) => setUpdateContent(e.target.value)}
                    placeholder="Enter update content..."
                    rows={5}
                    required
                  />
                </div>
                
                {updateType && (
                  <div className="grid grid-cols-1 gap-2">
                    <Label htmlFor="attachmentUrl">{getAttachmentFieldLabel()}</Label>
                    <Input 
                      id="attachmentUrl"
                      value={attachmentUrl}
                      onChange={(e) => setAttachmentUrl(e.target.value)}
                      placeholder="https://..."
                      type="url"
                    />
                  </div>
                )}
                
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    type="checkbox"
                    id="sendToParticipants"
                    checked={sendEmailToParticipants}
                    onChange={(e) => setSendEmailToParticipants(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <Label htmlFor="sendToParticipants" className="text-sm font-normal">
                    Send email notification to all participants
                  </Label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-2 mt-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowUpdateForm(false);
                    resetUpdateForm();
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={isSubmittingUpdate || !updateType || !updateContent}
                >
                  {isSubmittingUpdate ? "Sending..." : "Send Update"}
                </Button>
              </div>
            </form>
          </div>
        ) : (
          <div className="space-y-6">
            {isUpdatesLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : eventUpdates?.data && eventUpdates.data.length > 0 ? (
              <div className="space-y-4">
                {eventUpdates.data.map((update) => (
                  <div key={update.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex items-start gap-3">
                      <div className="rounded-full bg-primary/10 p-2 text-primary">
                        {getUpdateTypeIcon(update.update_type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <Badge variant="outline" className="capitalize">
                            {getUpdateTypeLabel(update.update_type)}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {(() => {
                              try {
                                return format(new Date(update.created_at), "MMM d, yyyy 'at' h:mm a");
                              } catch (e) {
                                return update.created_at;
                              }
                            })()}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-line">{update.update_content}</p>
                        {update.attachment_url && (
                          <a 
                            href={update.attachment_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center text-sm text-primary hover:underline"
                          >
                            <Link className="h-4 w-4 mr-1" />
                            View Attachment
                          </a>
                        )}
                        <div className="mt-2 text-xs text-muted-foreground">
                          Posted by {update.created_by}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-700">No Updates Yet</h3>
                <p className="text-sm text-slate-500 mt-1 mb-4">
                  Create your first update to keep participants informed
                </p>
                <Button 
                  onClick={() => setShowUpdateForm(true)}
                  className="bg-primary hover:bg-primary/90"
                >
                  <SendHorizonal className="mr-2 h-4 w-4" />
                  Create Update
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function CommunityEventDetails({ session, profile }: { session: any; profile: any }) {
  const { slug } = useParams();
  const [activeTab, setActiveTab] = useState<string>("participants");
  const navigate = useNavigate();

  const { data: event, isLoading } = useQuery<CommunityEventResponse>({
    queryKey: ["community-event", slug],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/campaign/community-events/${slug}`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch event");
      }
      return response.json();
    },
    enabled: !!slug && !!session?.access_token,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto py-10">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-[250px]" />
            <Skeleton className="h-4 w-[200px]" />
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto py-10">
        <div className="flex flex-col items-center justify-center py-20">
          <div className="rounded-full bg-red-100 p-3">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-4 text-lg font-medium">Event not found</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            The event you're looking for doesn't exist or you don't have access to it.
          </p>
          <Button
            onClick={() => navigate("/dashboard/community-events")}
            className="mt-4"
            variant="outline"
          >
            Go back to events
          </Button>
        </div>
      </div>
    );
  }

  // Get status badge color based on event status
  const getStatusBadgeColor = (status: string | undefined | null) => {
    if (!status) return 'bg-gray-500/10 text-gray-700 border-gray-300';
    
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-500/10 text-green-700 border-green-300';
      case 'draft':
        return 'bg-gray-500/10 text-gray-700 border-gray-300';
      case 'completed':
        return 'bg-blue-500/10 text-blue-700 border-blue-300';
      case 'cancelled':
        return 'bg-red-500/10 text-red-700 border-red-300';
      default:
        return 'bg-gray-500/10 text-gray-700 border-gray-300';
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate("/dashboard/community-events")}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold">{event.data.title}</h1>
            <Badge 
              variant="outline" 
              className={`capitalize ${getStatusBadgeColor(event.data.is_active ? 'active' : 'inactive')}`}
            >
              {event.data.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/dashboard/community-events/${slug}/edit`)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit Event
            </Button>
          </div>
        </div>
        <div className="mt-2 flex items-center space-x-4 text-sm text-muted-foreground">
          <div className="flex items-center">
            <CalendarIcon className="mr-1 h-4 w-4" />
            {(() => {
              try {
                return event.data.event_date 
                  ? format(new Date(event.data.event_date), "MMMM d, yyyy") 
                  : "No start date";
              } catch (e) {
                return event.data.event_date || "No start date";
              }
            })()}
          </div>
          <div className="flex items-center">
            <Clock className="mr-1 h-4 w-4" />
            {event.data.start_time ? (
              <>
                {(() => {
                  try {
                    return format(new Date(`2000-01-01T${event.data.start_time}`), "h:mm a");
                  } catch (e) {
                    return event.data.start_time;
                  }
                })()}
                {event.data.duration && ` (${event.data.duration} minutes)`}
              </>
            ) : (
              "No time specified"
            )}
          </div>
          <div className="flex items-center">
            <MapPin className="mr-1 h-4 w-4" />
            {event.data.format_details || "No location specified"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Event Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {event.data.images && event.data.images.length > 0 && (
                <div className="rounded-md overflow-hidden h-[300px] bg-slate-100">
                  <img
                    src={event.data.images[0]}
                    alt={event.data.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <h3 className="font-medium">Description</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {event.data.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium">Date & Time</h3>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <CalendarIcon className="h-4 w-4" />
                      <span>
                        {(() => {
                          try {
                            return format(new Date(event.data.event_date), "MMMM d, yyyy");
                          } catch (e) {
                            return event.data.event_date;
                          }
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground mt-1">
                      <Clock className="h-4 w-4" />
                      <span>
                        {(() => {
                          try {
                            return format(new Date(`2000-01-01T${event.data.start_time}`), "h:mm a");
                          } catch (e) {
                            return event.data.start_time;
                          }
                        })()}
                        {event.data.duration && ` (${event.data.duration} minutes)`}
                      </span>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-medium">Format</h3>
                    <p className="text-sm text-muted-foreground">
                      {event.data.format}
                      {event.data.format_details && ` - ${event.data.format_details}`}
                    </p>
                  </div>

                  <div>
                    <h3 className="font-medium">Event Type</h3>
                    <p className="text-sm text-muted-foreground">
                      {event.data.event_type}
                    </p>
                  </div>

                  <div>
                    <h3 className="font-medium">Registration Deadline</h3>
                    <p className="text-sm text-muted-foreground">
                      {event.data.deadline
                        ? (() => {
                            try {
                              return format(new Date(event.data.deadline), "MMM d, yyyy");
                            } catch (e) {
                              return event.data.deadline;
                            }
                          })()
                        : "No deadline"}
                    </p>
                  </div>

                  <div>
                    <h3 className="font-medium">Prerequisites</h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">
                      {event.data.prerequisites && event.data.prerequisites.length > 0
                        ? event.data.prerequisites.join("\n")
                        : "None specified"}
                    </p>
                  </div>

                  <div>
                    <h3 className="font-medium">Beneficiary Types</h3>
                    <p className="text-sm text-muted-foreground">
                      {event.data.beneficiary_types && event.data.beneficiary_types.length > 0
                        ? event.data.beneficiary_types.join(", ")
                        : "Not specified"}
                    </p>
                  </div>

                  <div>
                    <h3 className="font-medium">Contact Email</h3>
                    <p className="text-sm text-muted-foreground">
                      {event.data.npo?.email || "Not provided"}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-medium">Contact Phone</h3>
                    <p className="text-sm text-muted-foreground">
                      Not provided
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="participants">Participants</TabsTrigger>
              <TabsTrigger value="updates">Event Updates</TabsTrigger>
            </TabsList>
            <TabsContent value="participants" className="mt-6">
              <ParticipantsList eventId={event.data.id.toString()} session={session} profile={profile} />
            </TabsContent>
            <TabsContent value="updates" className="mt-6">
              <EventUpdates eventId={event.data.id.toString()} session={session} profile={profile} />
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Event Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <Badge 
                    variant="outline" 
                    className={`capitalize ${getStatusBadgeColor(event.data.is_active ? 'active' : 'inactive')}`}
                  >
                    {event.data.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Capacity</span>
                  <span className="font-medium">{event.data.max_participants || "Unlimited"}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Registered</span>
                  <span className="font-medium">{event.data.participants_count?.[0]?.count || 0}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Available Spots</span>
                  <span className="font-medium">
                    {event.data.max_participants 
                      ? Math.max(0, event.data.max_participants - (event.data.participants_count?.[0]?.count || 0))
                      : "Unlimited"}
                  </span>
                </div>
                {event.data.deadline && (
                  <>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Registration Closes</span>
                      <span className="font-medium">
                        {(() => {
                          try {
                            return format(new Date(event.data.deadline), "MMM d, yyyy");
                          } catch (e) {
                            return event.data.deadline;
                          }
                        })()}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Event Organizer</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-4">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={event.data.npo?.logo_url || undefined} />
                  <AvatarFallback>
                    {event.data.npo?.org_name
                      ? event.data.npo.org_name.substring(0, 2).toUpperCase()
                      : "OR"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{event.data.npo?.org_name || "Not specified"}</p>
                  {event.data.npo?.email && (
                    <p className="text-sm text-muted-foreground">{event.data.npo.email}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {event.data.format_details && (
            <Card>
              <CardHeader>
                <CardTitle>Location</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="rounded-md border overflow-hidden h-[200px] bg-muted">
                    {/* Map placeholder - could be replaced with an actual map component */}
                    <div className="h-full w-full flex items-center justify-center bg-slate-100">
                      <MapPin className="h-8 w-8 text-muted-foreground" />
                    </div>
                  </div>
                  <p className="text-sm">{event.data.format_details}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
